/** feat-338 — Section 1, the summary strip. A suppressed summary shows "—" tiles with the hint. */
import React from 'react';
import type { Summary } from '../../../../services/questionAnalysisService';
import { formatMinutes, formatTargetMinutes, pct, QA_COPY } from './questionAnalysisFormat';
import { orDash, SuppressedValue } from './parts';

const Tile: React.FC<{ label: string; foot?: string; suppressed?: boolean; children?: React.ReactNode }> = ({
  label,
  foot,
  suppressed,
  children,
}) => (
  <div className="bg-white rounded-2xl border border-softBorder shadow-sm p-4 min-w-0" data-testid="summary-tile">
    <p className="text-xs font-bold text-inkMuted">{label}</p>
    {suppressed ? (
      <div className="text-3xl font-display font-semibold mt-1 leading-none">
        <SuppressedValue />
      </div>
    ) : (
      <p className="text-3xl font-display font-semibold text-ink mt-1 leading-none">{children}</p>
    )}
    {foot && <p className="text-xs text-inkMuted mt-1.5">{foot}</p>}
  </div>
);

const SummaryStrip: React.FC<{ summary: Summary }> = ({ summary }) => {
  const target = formatTargetMinutes(summary.target_lesson_ms);
  const labels = [
    { label: QA_COPY.sumLearners },
    { label: QA_COPY.sumLessons },
    { label: QA_COPY.sumRight, foot: QA_COPY.sumRightFoot },
    { label: QA_COPY.sumTypical, foot: QA_COPY.sumTypicalFoot(target) },
    { label: QA_COPY.sumOver(target), foot: QA_COPY.sumOverFoot },
  ];
  const grid = 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-5';
  if (summary.suppressed) {
    return (
      <div className={grid} aria-label={QA_COPY.summary} data-testid="summary">
        {labels.map((l) => (
          <Tile key={l.label} label={l.label} foot={l.foot} suppressed />
        ))}
      </div>
    );
  }
  const values = [
    summary.learners,
    orDash(summary.lessons_completed),
    orDash(pct(summary.first_try_correct_rate)),
    orDash(formatMinutes(summary.median_lesson_active_ms)),
    orDash(pct(summary.lessons_over_target_rate)),
  ];
  return (
    <div className={grid} aria-label={QA_COPY.summary} data-testid="summary">
      {labels.map((l, i) => (
        <Tile key={l.label} label={l.label} foot={l.foot}>
          {values[i]}
        </Tile>
      ))}
    </div>
  );
};

export default SummaryStrip;
